import Partner from "@/domains/Partners/Partner";
import Person from "@/domains/Persons/Person";

import BasePageFilter from "@/domains/BasePageFilter/BasePageFilter";
import DateRange      from '@/core/types/DateRange';
import Core from '@/core/Core';
import TendersService from "@/domains/Tenders/TendersService";

class TendersPageFilter extends BasePageFilter {
    static Events = {
        Updated: 'tenders-page-filter:updated',
    } as const;
    protected storageKey: string = 'filters:tenders';

    private _isMine: boolean;
    responsible: Person;
    seller: Partner;
    buyer: Partner;
    desiredReceiptPeriod: DateRange;
    endAtPeriod: DateRange;
    isPreorderNeeded: { id: number; name: string; value: any };
    winnersCategory: { id: number; name: string; value: any };
    bidStatus : { id: string, name: string }

    // Константы дефолтных значений
    private static readonly DEFAULT_IS_MINE = false;
    private static readonly DEFAULT_WINNERS_CATEGORY_ID = 0;
    private static readonly DEFAULT_IS_PREORDER_NEEDED_ID  = 3;
    private static readonly DEFAULT_BID_STATUS = TendersService.BID_STATUS.ALL;

    /**
     * Геттер для isMine
     */
    get isMine(): boolean {
        return this._isMine;
    }

    /**
     * Сеттер для isMine
     * Если true - устанавливаем responsible = текущий пользователь
     */
    set isMine(value: boolean) {
        this._isMine = value;

        if (value === true) {
            this.responsible = Core.currentUser;
        } else {
            this.responsible = undefined;
        }
    }

    reset(): void {
        this.isMine = TendersPageFilter.DEFAULT_IS_MINE;
        this.responsible = undefined;

        this.buyer = Core.currentPartner.isBuyer ? Core.currentPartner : undefined;
        this.seller = undefined;

        this.desiredReceiptPeriod = new DateRange();
        this.endAtPeriod = new DateRange();

        this.winnersCategory = TendersService.WINNERS_CATEGORIES.get(TendersPageFilter.DEFAULT_WINNERS_CATEGORY_ID);
        this.isPreorderNeeded = Core.dictionaries.tenderIsPreorderNeeded.get(TendersPageFilter.DEFAULT_IS_PREORDER_NEEDED_ID);
        this.bidStatus = TendersService.BID_STATUS_FILTER_OPTIONS.find(
          option => option.id === TendersPageFilter.DEFAULT_BID_STATUS,
        );
    }

    protected deserialize(json: string): void {
        const parsed = JSON.parse(json);

        // Восстанавливаем примитивы
        this.isMine = parsed.isMine !== undefined ? parsed.isMine : TendersPageFilter.DEFAULT_IS_MINE;
        this.responsible = Core.utils.hydrate(parsed.responsible, Person.fromServer);
        this.seller = Core.utils.hydrate(parsed.seller, Partner.fromServer);

        // Если текущий пользователь покупатель, и он не привилегированный, и выбранный покупатель не текущий,
        // то значит это Ира перешла из Сибирского здоровья в Технологии красоты, чтобы протестить работу фильтра,
        // и мы должны подставить текущего партнёра в фильтр
        if (Core.currentPartner?.isBuyer && Core.currentUser?.isPrivileged === false && Core.currentPartner.id !== parsed?.buyer?.id) this.buyer = Core.currentPartner;
        else if (parsed?.buyer) this.buyer =  Core.utils.hydrate(parsed.buyer, Partner.fromServer);
        else if (Core.currentPartner.isBuyer) this.buyer = Core.currentPartner;
        else this.buyer = undefined;


        // Мапим на словари
        this.isPreorderNeeded = parsed.isPreorderNeeded !== undefined
            ? parsed.isPreorderNeeded
            : Core.dictionaries.tenderIsPreorderNeeded.get(TendersPageFilter.DEFAULT_IS_PREORDER_NEEDED_ID);

        this.winnersCategory = parsed.winnersCategory !== undefined
            ? parsed.winnersCategory
            :  TendersService.WINNERS_CATEGORIES.get(TendersPageFilter.DEFAULT_WINNERS_CATEGORY_ID);

        // Восстанавливаем сложные типы
        this.desiredReceiptPeriod = Core.utils.isNullOrUndefined(parsed.desiredReceiptPeriod) === false
            ? DateRange.fromObject(parsed.desiredReceiptPeriod) as DateRange
            : new DateRange();
        this.endAtPeriod = Core.utils.isNullOrUndefined(parsed.endAtPeriod) === false
            ? DateRange.fromObject(parsed.endAtPeriod) as DateRange
            : new DateRange();

      this.bidStatus = TendersService.BID_STATUS_FILTER_OPTIONS.find(
        option => option.id === TendersPageFilter.DEFAULT_BID_STATUS,
      );

    }

    /**
     * Подготовка параметров для API запроса
     */
    getRequestParams(): Record<string, any> {
        const params: any = {};

        if (this.responsible?.id) params.responsible = { id: this.responsible.id };
        if (this.seller?.id) params.seller = { id: this.seller.id };
        if (this.buyer?.id) params.buyer = { id: this.buyer.id };
        if (this.desiredReceiptPeriod?.start && this.desiredReceiptPeriod?.end) params.desiredReceiptPeriod = this.desiredReceiptPeriod;
        if (this.endAtPeriod?.start && this.endAtPeriod?.end) params.endAtPeriod = this.endAtPeriod;
        if (typeof this.isPreorderNeeded?.value === 'boolean') params.isPreorderNeeded = this.isPreorderNeeded.value;
        if (this.winnersCategory.value !== 'all') { params.winnersCategory = this.winnersCategory }
        if (this.bidStatus.id !=='all') { params.bidStatus = this.bidStatus }

        return params;
    }
}

export default TendersPageFilter;
